import React, {
	forwardRef,
	KeyboardEvent,
	MouseEventHandler,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { IconProps, palette, SectionTitle } from '@sharefiledev/flow-web';
import {
	ComponentTypes,
	docgenFullScreenEditFlowId,
} from '../../../../common/componentUtils';
import { DocgenDragEvent } from '../../DocgenDragEvent';

interface DocgenDragEvent {
	setIsDragging(value: any): void;
	isDragging(): boolean;
	clear(): void;
}
import {
	Description,
	Field as StyledField,
	FieldLabel,
	Fields as StyledFields,
	FieldsSectionWrapper,
	PlaceHolderComponent,
	Title,
} from './FieldsSection.styled';

type FieldType = {
	type: ComponentTypes;
	label: string;
	icon: React.FC<IconProps>;
	color?: string;
	draggable?: boolean;
};

interface FieldsSectionProps {
	children: React.ReactNode;
}

export const FieldsSection = ({ children }: FieldsSectionProps) => {
	return <FieldsSectionWrapper>{children}</FieldsSectionWrapper>;
};

interface FieldSectionInfoProps {
	title: string;
	description: string;
}

const FieldSectionInfo = ({ title, description }: FieldSectionInfoProps) => {
	return (
		<>
			<SectionTitle>
				<Title>{title}</Title>
			</SectionTitle>
			<Description>{description}</Description>
		</>
	);
};

interface FieldsProps<T> {
	fields: T[];
	selectedField: string | { name: string } | null;
	onClick?: (type: ComponentTypes, value?: any) => void;
}

const Fields = <T extends FieldType>({
	fields,
	selectedField,
	onClick,
}: FieldsProps<T>) => {
	const [selectedLeftPanelComponent, setSelectedLeftPanelComponent] = useState(null);
	const placeholderComponentRef = useRef<HTMLDivElement>(null);
	const parentRef = useRef<HTMLElement>(null);
	const selectedElementRef = useRef<HTMLDivElement>(null);
	const overlayClientRect = useRef(null);
	const [showPlaceHolderComponent, setShowPlaceHolderComponent] = useState(false);
	const [keyboardDragging, setKeyboardDragging] = useState(false);
	const [keyboardPosition, setKeyboardPosition] = useState({ x: 0, y: 0 });
	const [currentDraggingField, setCurrentDraggingField] = useState<T | null>(null);
	const [isOverDocument, setIsOverDocument] = useState(false);
	const keyboardDragStep = 10;

	// Helper function to clean up all dragging state
	const cleanupDraggingState = useCallback(() => {
		setKeyboardDragging(false);
		setShowPlaceHolderComponent(false);
		setCurrentDraggingField(null);
		setSelectedLeftPanelComponent(null);
		setIsOverDocument(false);
		DocgenDragEvent.clear();
	}, []);

	const moveWithArrowKeys = useCallback(
		(arrowKey: string) => {
			setKeyboardPosition(prevPos => {
				let { x, y } = prevPos;

				switch (arrowKey) {
					case 'ArrowUp':
						y -= keyboardDragStep;
						break;
					case 'ArrowDown':
						y += keyboardDragStep;
						break;
					case 'ArrowLeft':
						x -= keyboardDragStep;
						break;
					case 'ArrowRight':
						x += keyboardDragStep;
						break;
				}

				return { x, y };
			});

			setTimeout(() => {
				if (parentRef.current && placeholderComponentRef.current) {
					const placeholderRect = placeholderComponentRef.current.getBoundingClientRect();
					parentRef.current.getBoundingClientRect();

					const currentX = placeholderRect.left;
					const currentY = placeholderRect.top;

					const pageElements = document.querySelectorAll(
						`#${docgenFullScreenEditFlowId} [id^="overlayer-page"]`
					);
					let foundOverlayElement = false;

					for (let i = 0; i < pageElements.length; i++) {
						const pageRect = pageElements[i].getBoundingClientRect();
						if (
							currentX >= pageRect.left &&
							currentX <= pageRect.right &&
							currentY >= pageRect.top &&
							currentY <= pageRect.bottom
						) {
							overlayClientRect.current = pageRect;
							setIsOverDocument(true);
							foundOverlayElement = true;
							break;
						}
					}

					if (!foundOverlayElement) {
						setIsOverDocument(false);
					}
				}
			}, 0);
		},
		[keyboardDragStep]
	);

	const startKeyboardDragging = useCallback((field: T, fieldElement: HTMLDivElement) => {
		setKeyboardDragging(true);
		setShowPlaceHolderComponent(true);
		setCurrentDraggingField(field);

		// Set initial position for the placeholder
		if (!parentRef.current) {
			parentRef.current = document.getElementById('tabs-container');
		}

		if (parentRef.current) {
			const { x: parentX, y: parentY } = parentRef.current.getBoundingClientRect();
			const { left, top } = fieldElement.getBoundingClientRect();

			setKeyboardPosition({
				x: left - parentX,
				y: top - parentY,
			});
		}

		// Set the icon for the placeholder
		setSelectedLeftPanelComponent(
			React.createElement(field.icon, { size: 16, color: field.color || palette.grey800 })
		);
		DocgenDragEvent.setIsDragging(true);
	}, []);

	// Function to handle drop on document
	const handleDropOnDocument = useCallback(() => {
		if (!currentDraggingField) {
			return;
		}

		if (parentRef.current && placeholderComponentRef.current) {
			const placeholderRect = placeholderComponentRef.current.getBoundingClientRect();

			// Get the exact position of the placeholder
			const dropX = placeholderRect.left + 8;
			const dropY = placeholderRect.top + 8;

			const pageElements = document.querySelectorAll(
				`#${docgenFullScreenEditFlowId} [id^="overlayer-page"]`
			);
			let targetElement = null;

			for (let i = 0; i < pageElements.length; i++) {
				const pageRect = pageElements[i].getBoundingClientRect();
				if (
					dropX >= pageRect.left &&
					dropX <= pageRect.right &&
					dropY >= pageRect.top &&
					dropY <= pageRect.bottom
				) {
					targetElement = pageElements[i];
					break;
				}
			}

			if (targetElement) {
				const mouseUpEvent = new MouseEvent('mouseup', {
					bubbles: true,
					cancelable: true,
					view: window,
					clientX: dropX,
					clientY: dropY,
				});

				targetElement.dispatchEvent(mouseUpEvent);
			} else {
				const visiblePageElement = Array.from(pageElements).find(element => {
					const rect = element.getBoundingClientRect();
					return rect.top <= window.innerHeight && rect.bottom >= 0;
				});

				if (visiblePageElement) {
					const mouseUpEvent = new MouseEvent('mouseup', {
						bubbles: true,
						cancelable: true,
						view: window,
						clientX: dropX,
						clientY: dropY,
					});

					visiblePageElement.dispatchEvent(mouseUpEvent);
				}
			}
		}

		// Clear selection after dropping
		onClick?.(null);
		cleanupDraggingState();
	}, [currentDraggingField, onClick, cleanupDraggingState]);

	const toggleFieldSelection = useCallback(
		(field: T) => {
			const isCurrentlySelected =
				(typeof selectedField === 'object' && selectedField?.name === field.type) ||
				selectedField === field.type;

			if (isCurrentlySelected) {
				onClick?.(null);
				cleanupDraggingState();
			} else {
				onClick?.(field.type, field);
			}
		},
		[onClick, selectedField, cleanupDraggingState]
	);

	const onMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		document.body.style.cursor = 'grabbing';
		const target = event.target as HTMLDivElement;
		target.style.cursor = 'grabbing';
		selectedElementRef.current = target;
		DocgenDragEvent.setIsDragging(true);
	}, []);

	const onMouseUp = useCallback<MouseEventHandler<HTMLDivElement>>(event => {
		document.body.style.cursor = 'default';
		const target = event.target as HTMLDivElement;
		target.style.cursor = 'pointer';
		selectedElementRef.current = null;
		DocgenDragEvent.clear();
	}, []);

	const onKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>, field: T) => {
			if (event.key === ' ' || event.key === 'Space') {
				event.preventDefault();

				if (keyboardDragging && isOverDocument) {
					handleDropOnDocument();
				} else {
					toggleFieldSelection(field);
				}
			} else if (
				(event.key === 'ArrowUp' ||
					event.key === 'ArrowDown' ||
					event.key === 'ArrowLeft' ||
					event.key === 'ArrowRight') &&
				field.draggable !== false
			) {
				const isSelected =
					(typeof selectedField === 'object' && selectedField?.name === field.type) ||
					selectedField === field.type;

				if (isSelected) {
					event.preventDefault();

					if (!keyboardDragging) {
						startKeyboardDragging(field, event.currentTarget);
					} else {
						moveWithArrowKeys(event.key);
					}
				}
			} else if (event.key === 'Escape' && keyboardDragging) {
				cleanupDraggingState();
			}
		},
		[
			keyboardDragging,
			isOverDocument,
			selectedField,
			toggleFieldSelection,
			startKeyboardDragging,
			moveWithArrowKeys,
			cleanupDraggingState,
			handleDropOnDocument,
		]
	);

	const onKeyUp = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key === ' ' || event.key === 'Enter') {
			event.preventDefault();
		}
	}, []);

	useEffect(() => {
		const resize = () => {
			overlayClientRect.current = document
				.querySelector(`#${docgenFullScreenEditFlowId} [id^="overlayer-page"]`)
				?.getBoundingClientRect();
		};

		window.addEventListener('resize', resize);

		return () => {
			window.removeEventListener('resize', resize);
		};
	}, []);

	useEffect(() => {
		const placeHolderComponent = placeholderComponentRef.current;

		const onMouseup = () => {
			const selectedElement = selectedElementRef.current;
			document.body.style.cursor = 'default';
			if (selectedElement) {
				selectedElement.style.cursor = 'pointer';
				selectedElementRef.current = null;
				DocgenDragEvent.clear();
			}
			if (placeHolderComponent && !keyboardDragging) {
				setShowPlaceHolderComponent(false);
			}
		};

		const onMouseMove = event => {
			if (!selectedElementRef.current) {
				return;
			}

			const parentElement = parentRef.current;
			if (!parentElement) {
				return;
			}

			const { x: parentX, y: parentY } = parentElement.getBoundingClientRect();
			const { clientX, clientY } = event;

			const positionLeft = clientX - parentX - 8;
			const positionTop = clientY - parentY - 8;

			if (placeHolderComponent && !keyboardDragging) {
				placeHolderComponent.style.left = positionLeft + 'px';
				placeHolderComponent.style.top = positionTop + 'px';

				const pageElements = document.querySelectorAll(
					`#${docgenFullScreenEditFlowId} [id^="overlayer-page"]`
				);
				let isCurrentlyOverDocument = false;

				// Check if the mouse is over any page
				for (let i = 0; i < pageElements.length; i++) {
					const pageRect = pageElements[i].getBoundingClientRect();
					if (
						clientX >= pageRect.left &&
						clientX <= pageRect.right &&
						clientY >= pageRect.top &&
						clientY <= pageRect.bottom
					) {
						isCurrentlyOverDocument = true;
						// Set the current overlay rect to be the one we're hovering over
						overlayClientRect.current = pageRect;
						break;
					}
				}

				setIsOverDocument(isCurrentlyOverDocument);

				if (isCurrentlyOverDocument) {
					setShowPlaceHolderComponent(false);
				} else {
					setShowPlaceHolderComponent(true);
				}
			}
		};

		const onGlobalKeydown = (event: KeyboardEvent) => {
			if (keyboardDragging && currentDraggingField) {
				if (event.key === ' ' || event.key === 'Space') {
					event.preventDefault();

					if (isOverDocument) {
						handleDropOnDocument();
					} else {
						onClick?.(null);
						cleanupDraggingState();
					}
				} else if (
					event.key === 'ArrowUp' ||
					event.key === 'ArrowDown' ||
					event.key === 'ArrowLeft' ||
					event.key === 'ArrowRight'
				) {
					event.preventDefault();
					moveWithArrowKeys(event.key);
				} else if (event.key === 'Escape') {
					event.preventDefault();
					cleanupDraggingState();
				}
			}
		};

		window.document.addEventListener('mouseup', onMouseup);
		window.document.addEventListener('mousemove', onMouseMove);
		window.document.addEventListener('keydown', onGlobalKeydown as any);

		return () => {
			window.document.removeEventListener('mouseup', onMouseup);
			window.document.removeEventListener('mousemove', onMouseMove);
			window.document.removeEventListener('keydown', onGlobalKeydown as any);
		};
	}, [
		keyboardDragging,
		moveWithArrowKeys,
		currentDraggingField,
		keyboardPosition,
		onClick,
		cleanupDraggingState,
		isOverDocument,
		handleDropOnDocument,
	]);

	useEffect(() => {
		if (selectedField === null && keyboardDragging) {
			cleanupDraggingState();
		}
	}, [selectedField, keyboardDragging, cleanupDraggingState]);

	useEffect(() => {
		if (!parentRef.current) {
			parentRef.current = document.getElementById('tabs-container');
		}
	}, []);

	useEffect(() => {
		if (keyboardDragging && placeholderComponentRef.current) {
			const { x, y } = keyboardPosition;
			placeholderComponentRef.current.style.left = x + 'px';
			placeholderComponentRef.current.style.top = y + 'px';
		}
	}, [keyboardPosition, keyboardDragging]);

	return (
		<>
			<StyledFields>
				{fields.map((field, index) => {
					const { type, label, icon, color, draggable = true } = field;
					let isSelected = false;
					if (typeof selectedField === 'object') {
						isSelected = selectedField?.name === type;
					} else {
						isSelected = selectedField === type;
					}

					return (
						<Field
							key={index}
							onClick={() => {
								onClick?.(type, field);
							}}
							color={color}
							isSelected={isSelected}
							label={label}
							icon={icon}
							onMouseDown={event => {
								if (draggable) {
									onMouseDown(event);
								}
							}}
							onMouseUp={event => {
								if (draggable) {
									onMouseUp(event);
								}
							}}
							onKeyDown={event => onKeyDown(event, field)}
							onKeyUp={onKeyUp}
							setSelectedLeftPanelComponent={setSelectedLeftPanelComponent}
							draggable={draggable}
						/>
					);
				})}
			</StyledFields>
			<GenericPlaceHolderComponent
				ref={placeholderComponentRef}
				selectedComponents={selectedLeftPanelComponent}
				showPlaceHolderComponent={showPlaceHolderComponent || keyboardDragging}
				isOverDocument={isOverDocument}
			/>
		</>
	);
};

interface FieldProps {
	onClick: () => void;
	color: string | undefined;
	isSelected: boolean;
	label: string;
	icon: React.FC<IconProps>;
	onMouseDown: React.MouseEventHandler<HTMLDivElement>;
	onMouseUp: React.MouseEventHandler<HTMLDivElement>;
	onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
	onKeyUp: React.KeyboardEventHandler<HTMLDivElement>;
	setSelectedLeftPanelComponent: React.Dispatch<React.SetStateAction<any>>;
	draggable?: boolean;
}

const Field = ({
	onClick,
	color,
	isSelected,
	icon: Icon,
	label,
	onMouseDown,
	onMouseUp,
	onKeyDown,
	onKeyUp,
	setSelectedLeftPanelComponent,
	draggable = true,
}: FieldProps) => {
	const IconLabelWrapper = Icon && <Icon size={16} color={palette.grey800} />;
	return (
		<StyledField
			optionColor={color}
			isSelected={isSelected}
			onMouseDown={event => {
				setSelectedLeftPanelComponent(IconLabelWrapper);
				onMouseDown(event);
				onClick();
			}}
			onMouseUp={event => {
				setSelectedLeftPanelComponent(null);
				onMouseUp(event);
			}}
			onKeyDown={onKeyDown}
			onKeyUp={onKeyUp}
			tabIndex={0}
			aria-label={`${label} field${draggable ? ' (draggable)' : ''}`}
			role="button"
			data-draggable={draggable}
		>
			{Icon && <Icon size={16} color={color} />}
			<FieldLabel>{label}</FieldLabel>
		</StyledField>
	);
};

FieldsSection.Info = FieldSectionInfo;
FieldsSection.Fields = Fields;

interface GenericPlaceHolderComponentProps {
	selectedComponents: React.ReactNode;
	showPlaceHolderComponent: boolean;
	isOverDocument?: boolean;
}

const GenericPlaceHolderComponent = forwardRef(
	(
		{ selectedComponents, showPlaceHolderComponent }: GenericPlaceHolderComponentProps,
		ref: any
	) => {
		return (
			<PlaceHolderComponent
				ref={ref}
				optionColor={palette.grey800}
				showPlaceHolderComponent={showPlaceHolderComponent}
				isSelected
				data-testid="placeholder-component"
				role="presentation"
				aria-hidden="true"
			>
				{selectedComponents}
			</PlaceHolderComponent>
		);
	}
);

export { DocgenDragEvent, type FieldType };
